import React from 'react';
import { connect } from 'react-redux';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import PropTypes from 'prop-types';
import {
  claimAvailable,
  isChampvaProviderClaim,
  withClaimStatusMetaIfEnabled,
} from '~/platform/shared/claims-appeals';
import { clearNotification } from '../actions';
import ClaimDetailLayout from '../components/ClaimDetailLayout';
import AdditionalEvidencePage from '../components/claim-files-tab/AdditionalEvidencePage';
import ClaimFileHeader from '../components/claim-files-tab/ClaimFileHeader';
import ClaimFilesLayout from '../components/claim-files-tab/ClaimFilesLayout';
import OtherWaysToSendYourDocuments from '../components/claim-files-tab-v2/OtherWaysToSendYourDocuments';
import FileSubmissionsInProgress from '../components/claim-files-tab-v2/FileSubmissionsInProgress';
import FilesReceived from '../components/claim-files-tab-v2/FilesReceived';
import FilesWeCouldntReceiveEntryPoint from '../components/claim-files-tab-v2/FilesWeCouldntReceiveEntryPoint';
import ReviewRequestsAlert from '../components/claim-files-tab/ReviewRequestsAlert';
import withRouter from '../utils/withRouter';

import {
  isClaimOpen,
  setPageFocus,
  setTabDocumentTitle,
} from '../utils/helpers';
import { getFilesNeeded } from '../utils/trackedItemContent';
import {
  setUpPage,
  isTab,
  setPageFocus as scrollToElement,
} from '../utils/page';
import { ANCHOR_LINKS, QUICK_SUBMIT_GUIDANCE } from '../constants';

// CONSTANTS
const NEED_ITEMS_STATUS = 'NEEDED_FROM_';

const hasRequestedDocuments = applicants =>
  (applicants || []).some(applicant => applicant?.documentsRequested === true);

class FilesPage extends React.Component {
  componentDidMount() {
    const { claim, location } = this.props;
    // Only set the document title at mount-time if the claim is already available.
    if (!this.props.asTabPanel && claimAvailable(claim))
      setTabDocumentTitle(claim, 'Files');

    if (location?.hash === '') {
      if (!this.props.asTabPanel) {
        setTimeout(() => {
          const { lastPage, loading } = this.props;
          setPageFocus(lastPage, loading);
        });
      }
    } else if (location?.hash) {
      // Handle hash navigation on mount (for direct navigation with hash)
      setTimeout(() => {
        this.scrollToSection();
      }, 100);
    }
  }

  componentDidUpdate(prevProps) {
    const { claim, lastPage, loading, location } = this.props;

    if (!this.props.asTabPanel) {
      if (!loading && prevProps.loading && !isTab(lastPage)) {
        setUpPage(false);
      }
      // Set the document title when loading completes.
      //   If loading was successful it will display a title specific to the claim.
      //   Otherwise it will display a default title of "Files for Your Claim".
      if (loading !== prevProps.loading) {
        setTabDocumentTitle(claim, 'Files');
      }
    }

    // Scroll to hash anchor after content has loaded (from external navigation)
    if (!loading && prevProps.loading && location?.hash) {
      // Add small delay to ensure DOM is fully rendered
      setTimeout(() => {
        this.scrollToSection();
      }, 100);
    }
  }

  getPageContent() {
    const { claim, closedTrackedItemsEnabled, cstChampvaCustomContentEnabled } =
      this.props;

    const displayClaim = withClaimStatusMetaIfEnabled(
      claim,
      cstChampvaCustomContentEnabled,
    );

    // Return null if the claim/ claim.attributes dont exist
    if (!claimAvailable(displayClaim)) {
      return null;
    }

    const {
      closeDate,
      status,
      supportingDocuments,
      trackedItems,
      evidenceSubmissions,
      claimStatusMeta,
    } = displayClaim.attributes;

    const isOpen = isClaimOpen(status, closeDate);
    const isChampvaFlow =
      cstChampvaCustomContentEnabled && isChampvaProviderClaim(displayClaim);
    const hasAnyRequestedDocuments = hasRequestedDocuments(
      displayClaim.attributes?.cstChampvaApplicants,
    );
    // Applicant-level only -- ignores Step 1/Step 2 so a processed applicant
    // isn't blocked by a still-pending sibling.
    const isChampvaUploaderOpen = hasAnyRequestedDocuments;
    const isUploaderOpen = isChampvaFlow ? isChampvaUploaderOpen : isOpen;
    const useSimpleFilesLayout = claimStatusMeta?.files?.simpleLayout;
    const useUploadFirstLayout = claimStatusMeta?.files?.uploadFirstLayout;
    const uploadTitle = claimStatusMeta?.files?.uploadTitle;
    const mailFaxDetailsLabel =
      claimStatusMeta?.files?.detailsLabel ||
      'If you want to submit your documents by mail or fax';

    const documentsTurnedIn = trackedItems.filter(
      item => !item.status.startsWith(NEED_ITEMS_STATUS),
    );

    documentsTurnedIn.push(...supportingDocuments);
    documentsTurnedIn.sort((a, b) => {
      if (a.date === b.date) return -1;
      return a.date > b.date ? -1 : 1;
    });

    let filesContent;
    if (isChampvaFlow) {
      filesContent = (
        <>
          <AdditionalEvidencePage
            additionalEvidenceTitle={
              uploadTitle || 'Upload supporting documents'
            }
            hideOtherWaysLink
            forceUploaderOpen={isUploaderOpen}
          />
          {isUploaderOpen && (
            <va-details id="mail-fax-details" label={mailFaxDetailsLabel}>
              <OtherWaysToSendYourDocuments claim={displayClaim} hideHeader />
            </va-details>
          )}
          <div className="vads-u-margin-y--6 vads-u-border--1px vads-u-border-color--gray-light" />
          <FilesReceived claim={displayClaim} hideGeneratedChampvaForm />
          <div className="vads-u-margin-y--6 vads-u-border--1px vads-u-border-color--gray-light" />
          <FilesWeCouldntReceiveEntryPoint
            evidenceSubmissions={evidenceSubmissions}
          />
        </>
      );
    } else if (useSimpleFilesLayout) {
      filesContent = <OtherWaysToSendYourDocuments claim={displayClaim} />;
    } else if (useUploadFirstLayout) {
      filesContent = (
        <>
          <AdditionalEvidencePage
            additionalEvidenceTitle={
              uploadTitle || 'Upload supporting documents'
            }
            hideOtherWaysLink
          />
          <va-details id="mail-fax-details" label={mailFaxDetailsLabel}>
            <OtherWaysToSendYourDocuments claim={displayClaim} hideHeader />
          </va-details>
          <div className="vads-u-margin-y--6 vads-u-border--1px vads-u-border-color--gray-light" />
          <FileSubmissionsInProgress claim={displayClaim} />
          <div className="vads-u-margin-y--6 vads-u-border--1px vads-u-border-color--gray-light" />
          <FilesReceived claim={displayClaim} />
        </>
      );
    } else {
      return (
        <ClaimFilesLayout
          header={
            <ClaimFileHeader
              claim={displayClaim}
              isOpen={isOpen}
              isChampvaFlow={isChampvaFlow}
            />
          }
          uploadForm={
            <>
              {isOpen &&
                getFilesNeeded(trackedItems, closedTrackedItemsEnabled).length >
                  0 && <ReviewRequestsAlert useVaTabs={this.props.useVaTabs} />}
              {isOpen && QUICK_SUBMIT_GUIDANCE}
              <AdditionalEvidencePage additionalEvidenceTitle="Upload additional evidence" />
            </>
          }
          inProgress={<FileSubmissionsInProgress claim={displayClaim} />}
          filesReceived={<FilesReceived claim={displayClaim} />}
          filesWeCouldntReceive={
            <FilesWeCouldntReceiveEntryPoint
              evidenceSubmissions={evidenceSubmissions}
            />
          }
          otherWays={<OtherWaysToSendYourDocuments claim={displayClaim} />}
        />
      );
    }

    return (
      <div className="claim-files">
        <ClaimFileHeader
          claim={displayClaim}
          isOpen={isUploaderOpen}
          isChampvaFlow={isChampvaFlow}
        />
        {filesContent}
      </div>
    );
  }

  scrollToSection = () => {
    const { location } = this.props;
    const validHashes = [
      `#${ANCHOR_LINKS.fileSubmissionsInProgress}`,
      `#${ANCHOR_LINKS.filesWeCouldntReceive}`,
      `#${ANCHOR_LINKS.otherWaysToSendDocuments}`,
      `#${ANCHOR_LINKS.filesReceived}`,
      `#${ANCHOR_LINKS.addFiles}`,
    ];

    if (validHashes.includes(location.hash)) {
      scrollToElement(location.hash);
    }
  };

  render() {
    const { claim, cstChampvaCustomContentEnabled, loading, message } =
      this.props;
    const displayClaim = withClaimStatusMetaIfEnabled(
      claim,
      cstChampvaCustomContentEnabled,
    );

    let content = null;
    if (!loading && claim) {
      content = this.getPageContent();
    }

    if (this.props.asTabPanel) {
      return content;
    }

    return (
      <ClaimDetailLayout
        claim={displayClaim}
        loading={loading}
        clearNotification={this.props.clearNotification}
        currentTab="Files"
        message={message}
      >
        {content}
      </ClaimDetailLayout>
    );
  }
}

function mapStateToProps(state) {
  const claimsState = state.disability.status;

  return {
    loading: claimsState.claimDetail.loading,
    claim: claimsState.claimDetail.detail,
    closedTrackedItemsEnabled:
      state.featureToggles?.cst_surface_closed_tracked_items || false,
    cstChampvaCustomContentEnabled:
      state.featureToggles?.cst_champva_custom_content || false,
    message: claimsState.notifications.message,
    lastPage: claimsState.routing.lastPage,
    useVaTabs: toggleValues(state)[FEATURE_FLAG_NAMES.cstClaimDetailsVaTabs],
  };
}

const mapDispatchToProps = {
  clearNotification,
};

FilesPage.propTypes = {
  asTabPanel: PropTypes.bool,
  claim: PropTypes.object,
  clearNotification: PropTypes.func,
  closedTrackedItemsEnabled: PropTypes.bool,
  cstChampvaCustomContentEnabled: PropTypes.bool,
  lastPage: PropTypes.string,
  loading: PropTypes.bool,
  location: PropTypes.object,
  message: PropTypes.shape({
    body: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    title: PropTypes.string,
    type: PropTypes.string,
  }),
  useVaTabs: PropTypes.bool,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FilesPage),
);

export { FilesPage };
